import { useEffect, useState, useRef, useMemo } from "react";
import { SortDirections } from "client/interfaces/Misc";
import { Chevron, MenuIcon, Spinner } from "client/components/Iconset";
import { createTheme, Pagination, ThemeProvider } from '@mui/material';
import Panel from "client/components/layout/Panel";
import Spacer from "client/components/layout/Spacer";
import { LayoutOrientations } from "client/globals";
import DropdownProperty from "client/components/properties/DropdownProperty";
import { ScenarioSortAttributes } from "client/interfaces/Worklink";
import PlaceholderContent from "client/components/layout/PlaceholderContent";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import TuneIcon from '@mui/icons-material/Tune';
import LoopIcon from '@mui/icons-material/Loop';
import { useTypedDispatch, useTypedSelector } from "src/state/hooks";
import { StoreActions } from "client/state/store";
import ModalManager from "client/components/layout/ModalManager";
import BoolProperty from "client/components/properties/BoolProperty";
import APIUtility from "client/classes/APIUtility";
import GQLAppScenarios from "src/classes/api/GQL/scenarios/GQLAppScenarios";
import { IAppScenario, IListAppScenariosFilterVariables } from "src/classes/api/GQL/scenarios/gql";
import ScenarioListItem from "./ScenarioListItem";

const possiblePageSizes = [25, 50, 100];

interface IScenarioListProps
{
	searchFilter?: string;
}

const ScenarioList = (props: IScenarioListProps) => 
{
	const dispatch = useTypedDispatch();

	const pageSize = useTypedSelector(state => state.worklinkModule.settings.scenarioList.pageSize);
	const sortingMethod = useTypedSelector(state => state.worklinkModule.settings.scenarioList.sortingMethod);
	const sortingAttribute = useTypedSelector(state => state.worklinkModule.settings.scenarioList.sortingAttribute);
	const dashboardRevisionGroup = useTypedSelector(state => state.worklinkModule.dashboardRevisionGroup);

	const [scenarios, setScenarios] = useState<IAppScenario[]>([]);
	const [page, setPage] = useState<number>(0);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [refresh, setRefresh] = useState(0);
	const [hideIncompatibleScenarios, setHideIncompatibleScenarios] = useState(false);
	
	const contentRef = useRef<HTMLDivElement>(null);

	// Function to set scroll position
	const setScrollPosition = (position: number) => 
	{
		if (contentRef.current) 
		{
			contentRef.current.scrollTop = position;
		}
	};

	useEffect(() => 
	{
		setIsLoading(true);
		setScenarios([]);

		const filter: IListAppScenariosFilterVariables = {
			appList: true,
			published: true,
			fullText: props.searchFilter || undefined,
			orderBy: {
				attribute: sortingAttribute,
				direction: sortingMethod,
			},
			scopeCreateVersionRange: !hideIncompatibleScenarios ? ">=2.39.0" : undefined,
		};
		
		// Create an AbortController for this request
		const abortController = new AbortController();
		const timeoutId = setTimeout(() => 
		{
			if(dashboardRevisionGroup.id)
			{
				GQLAppScenarios.getScenario(dashboardRevisionGroup.id, abortController.signal).then((data: IAppScenario) => 
				{
					console.log("scenario", data);

					setScenarios(data.revisions || []);
					setTotalCount(data.revisions?.length || 0);
					setIsLoading(false);
				}).catch(error => 
				{
					if (error.networkError.name !== "AbortError")
					{
						setIsLoading(false);
						console.error(error, error.networkError);
					}
				});
			}
			else
			{
				GQLAppScenarios.listAppScenarios(page, filter, abortController.signal, pageSize).then((data: { scenarios: IAppScenario[], totalCount: number }) => 
				{
					// console.log("scenario list", data);
					
					// filter duplicates according to revision group names; TODO: this should be done on the server side
					const uniqueRevisionGroups = new Set<string>();
					const newScenarios = new Array<IAppScenario>();
					for(const scenario of data.scenarios)
					{
						if(scenario.revisionGroupName)
						{
							if(!uniqueRevisionGroups.has(scenario.revisionGroupName))
							{
								uniqueRevisionGroups.add(scenario.revisionGroupName);
								newScenarios.push(scenario);
							}
						}
						else
						{
							newScenarios.push(scenario);
						}
					}

					setScenarios(newScenarios);
					setTotalCount(data.totalCount);
					setIsLoading(false);
				}).catch(error => 
				{
					if (error.networkError.name !== "AbortError")
					{
						setIsLoading(false);
						console.error(error, error.networkError);
					}
				});
			}
		}, 100);
						
		// Cleanup function to abort the request when dependencies change or component unmounts
		return () => 
		{
			abortController.abort();
			clearTimeout(timeoutId);
		};
	}, [page, sortingMethod, props.searchFilter, pageSize, sortingAttribute, refresh, dashboardRevisionGroup, hideIncompatibleScenarios]);

	useEffect(() => 
	{
		setPage(0);
	}, [sortingMethod, props.searchFilter, pageSize, sortingAttribute]);

	useEffect(() =>
	{
		dispatch(StoreActions.worklink.setScenarioListSettings({ 
			sortingMethod: sortingAttribute === ScenarioSortAttributes.DatePublished ? SortDirections.Descending : SortDirections.Ascending 
		}));
	}, [sortingAttribute]);

	useEffect(() =>
	{
		setScrollPosition(0);
	}, [scenarios]);

	const scenarioList = useMemo(() => 
	{
		if(scenarios.length)
		{
			if(dashboardRevisionGroup.id)
			{
				return scenarios.map((scenario, index) => <ScenarioListItem key={scenario.id} scenario={scenario} revisions={scenarios} index={index} />);
			}
			else
			{
				return scenarios.map((scenario) => <ScenarioListItem key={scenario.id} scenario={scenario} />);
			}
		}
		else
		{
			return (
				<PlaceholderContent>
					{
						isLoading ?
							<Spinner />
							:
							<div>No scenarios found</div>
					}
				</PlaceholderContent>
			);
		}
	}, [scenarios, isLoading]);	

	if(dashboardRevisionGroup.id)
	{
		return (
			<Panel className={"scenarioListRoot"}
				data-name={"scenario-list-root"}
				orientation={LayoutOrientations.Vertical}
			>
				<Panel className={"scenarioListHeader largeText"}
					data-name={"scenario-list-header"}
					orientation={LayoutOrientations.Horizontal}
					size={{ y: "50px" }}
				>
					<Chevron data-name={"back-to-scenario-list"}
						onClick={() => dispatch(StoreActions.worklink.setDashboardRevisionGroup({ name: "", id: "" }))}
						rotate={90}
						scale={1.5}
					/>
					<div className={"largeText"}>
						{ dashboardRevisionGroup.name }
					</div>		
				</Panel>
				<div
					ref={contentRef}
					className={"scenarioListContent"}
					data-name={"scenario-list-content"}
				>
					{ scenarioList }
				</div>
			</Panel>
		);
	}
	else return (
		<Panel className={"scenarioListRoot"}
			data-name={"scenario-list-root"}
			orientation={LayoutOrientations.Vertical}
		>
			<Panel className={"scenarioListHeader largeText"}
				data-name={"scenario-list-header"}
				orientation={LayoutOrientations.Horizontal}
				size={{ y: "50px" }}
			>
				<TuneIcon
				  style={{
						cursor: "pointer",
					}}
					onClick={(e) => 
					{
						const rect = (e.target as HTMLElement).getBoundingClientRect();
						ModalManager.addModal({
							allowClickOut: true,
							showBackdrop: false,
							content: <ScenarioListSettings hideIncompatibleScenarios={hideIncompatibleScenarios} setHideIncompatibleScenarios={setHideIncompatibleScenarios} />, 
							rect: {
								x: rect.x - 3,
								y: rect.y + rect.height + 5,
							}
						})
					}}
				/>
				<DropdownProperty
					data-name={"sorting-attribute"}
					value={sortingAttribute}
					options={[
						{ display: "Name", value: ScenarioSortAttributes.Name },
						{ display: "Date Published", value: ScenarioSortAttributes.DatePublished }
					]}
					onChangeCallback={(value) => 
					{
						dispatch(StoreActions.worklink.setScenarioListSettings({ sortingAttribute: value as ScenarioSortAttributes }));
						APIUtility.saveWlcSettings();
					}}
				/>
				{
					sortingMethod === SortDirections.Ascending ?
						<ArrowDownwardIcon 
							cursor={"pointer"} 
							onClick={() => 
							{
								dispatch(StoreActions.worklink.setScenarioListSettings({ sortingMethod: SortDirections.Descending }));
								APIUtility.saveWlcSettings();
							}}
						/>
						:
						<ArrowUpwardIcon 
							cursor={"pointer"} 
							onClick={() => 
							{
								dispatch(StoreActions.worklink.setScenarioListSettings({ sortingMethod: SortDirections.Ascending }));
								APIUtility.saveWlcSettings();
							}}
						/>
				}
				<Spacer stretchTo={{ x: "100%" }} />
				<LoopIcon 
					cursor={"pointer"}
					onClick={() => { setRefresh(refresh + 1) }}
				/>
			</Panel>
			<div
				ref={contentRef}
				className={`scenarioListContent ${Object.keys(scenarios).length > 0 && isLoading ? "loading-scenarios" : ""}`}
				data-name={"scenario-list-content"}
			>
				{ scenarioList }
				{ (scenarios.length && isLoading) ? <div className="loaderGradient" /> : null }
			</div>
			<Panel data-name={"scenario-list-pagination-wrapper"}
				className={"scenarioListPagination"}
				orientation={LayoutOrientations.Horizontal}
				size={{ y: "50px" }}
			>
				<DropdownProperty
					data-name={"page-size"}
					value={pageSize.toString()}
					options={possiblePageSizes.map(size => ({
						display: size.toString(),
						value: size.toString()
					}))}
					onChangeCallback={(value) => dispatch(StoreActions.worklink.setScenarioListSettings({ pageSize: Number(value) }))}
					className={"fit"}
				/>
				<Spacer size={{ x: "12px" }} />
				<div data-name={"scenario-list-footer-label"}
					className={"scenarioListFooterLabel"}
				>
					Showing {(page + 1) * pageSize - pageSize + 1} to {Math.min((page + 1) * pageSize, totalCount)} of {totalCount} scenarios
				</div>						
				<ThemeProvider theme={createTheme({ palette: { mode: 'dark' }})}>				
					<Pagination
						count={Math.ceil(totalCount / pageSize)}
						page={page + 1}
						shape={"rounded"}
						size={"small"}
						onChange={(e, page) => setPage(page - 1)}
					/>
				</ThemeProvider>
			</Panel>
		</Panel>
	);
};

const ScenarioListSettings = (props: { hideIncompatibleScenarios: boolean, setHideIncompatibleScenarios: (value: boolean) => void }) =>
{
	const [hideIncompatibleScenarios, setHideIncompatibleScenarios] = useState(props.hideIncompatibleScenarios);

	useEffect(() =>
	{
		props.setHideIncompatibleScenarios(hideIncompatibleScenarios);
	}, [hideIncompatibleScenarios]);

	return (
		<div style={{ padding: "10px" }}>
			<BoolProperty
				labelAfterProperty={"Show incompatible scenarios"}
				value={hideIncompatibleScenarios}
				onChangeCallback={(value) => 
				{
					setHideIncompatibleScenarios(value);
				}}
				style={{ width: "200px", margin: 0 }}
			/>
		</div>
	)
}

export default ScenarioList;